import { InfoOutlineIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tag,
  Text,
} from "@chakra-ui/react";
import { Formik, FormikProvider } from "formik";
import React, { useMemo, useState } from "react";
import { RiCheckLine, RiCloseLine, RiLightbulbLine } from "react-icons/ri";
import type { Point } from "src/components/DocumentViewer/V2/types";
import {
  Question,
  QuestionProps,
} from "src/components/Inputs/QuestionDynamicInputs/Question";
import { AIValidationResult, DocumentAIResults } from "src/hooks/useDocumentAI";
import { useUserPermissions } from "src/hooks/useUserPermissions";
import { Question as QuestionService } from "src/services/formTemplate";
import * as AFF from "src/services/formTemplateFilters";
import { validateSingleQuestion } from "src/services/formTemplateValidations";
import { REQUIRED_FIELD_VALIDATION_ERROR } from "src/services/url/constants";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import { AISuggestion } from "./AISuggestion";
import { AvelaIntelligenceAlert } from "./AvelaIntelligenceAlert";
import { FamilyTabs } from "./FamilyTabs/FamilyTabs";
import { IdentityDocumentMatchHint } from "./IdentityDocumentMatchHint";
import { IdentityIntelligenceAlert } from "./IdentityIntelligenceAlert";
import { AdminQuestionAnswer } from "./QuestionAnswer";
import { getPreferredValue } from "./utils/aiSuggestionHelpers";
import {
  IdentityAnalyzeIdMatch,
  Profile,
} from "./utils/analyzeIdFormMatchUtils";

// Helper function for deep equality comparison
const isEqual = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => isEqual(item, b[index]));
  }
  if (typeof a === "object" && typeof b === "object") {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every((key) => isEqual(a[key], b[key]));
  }
  return false;
};

type VerificationDetailsProps = {
  verification: { id: string; label: string };
  questions: AF.Question<AF.WithId>[];
  answers: Record<string, any>;
  formId: string;
  profiles: Profile[];
  status: GQL.verification_status_enum | null;
  onStatusChange: (status: GQL.verification_status_enum) => void;
  canEditVerificationData: boolean;
  onAnswerUpdate: (
    questionId: string,
    value: any,
    skipAutoVerification?: boolean
  ) => void;
  onClose: (options?: {
    showToast?: boolean;
    toastType?: "info" | "success" | "error" | "warning";
  }) => void;
  aiResults?: DocumentAIResults | null;
  aiLoading?: boolean;
  aiError?: boolean;
  identityAiEnabled?: boolean;
  identityAiLoading?: boolean;
  identityAiError?: boolean;
  identityAiAnswerMatches?: IdentityAnalyzeIdMatch[];
  identityAiProfileMatches?: IdentityAnalyzeIdMatch[];
  onFindInDocument?: (polygons: readonly (readonly Point[])[]) => void;
};

// Helper function to create props for Question component
function createQuestionProps(
  question: AF.Question<AF.WithId>,
  formId: string,
  questionTitle: string
): QuestionProps {
  const mockApplicant: AFF.Types.Applicant = {
    birth_date: null,
    first_name: null,
  };

  const baseProps = {
    formId,
    question: questionTitle,
    isRequired: question.requirement === "Required",
    id: question.id,
    link_text: question.link_text,
    link_url: question.link_url,
    permissionLevel: question.permissionLevel,
    applicant: mockApplicant,
    kind: question.type as any,
  };

  switch (question.type) {
    case AF.DateType:
    case AF.NumberType:
      return { ...baseProps, constraints: question.constraints as any };

    case AF.SingleSelectType:
    case AF.MultiSelectType:
    case AF.GradesType:
      return {
        ...baseProps,
        options:
          (question as any).options?.map((o: any) => ({
            key: o.id,
            translate: o.translate_options === true,
            label: o.label,
            value: o.value,
            isArchived: o.isArchived,
          })) || [],
      };

    case AF.FileUploadType:
      return { ...baseProps, disableDocumentLibrary: false };

    case AF.AddressType:
      return { ...baseProps, disableAddressBook: false };

    case AF.CustomQuestionType:
      return {
        ...baseProps,
        customQuestionTypeId: (question as any).customQuestionTypeId,
        nestedQuestions: (question as any).nestedQuestions,
      };

    default:
      return baseProps;
  }
}

// Helper function to create Formik validation for a question
// Checks required fields first, then uses the shared validateSingleQuestion primitive
const createQuestionValidator = (question: AF.Question<AF.WithId>) => {
  return (values: Record<string, any>) => {
    const errors: Record<string, string> = {};
    const value = values[question.id];

    // Check required fields using the same utility as the main form
    if (question.requirement === "Required") {
      const answers: Record<string, any> = { [question.id]: value };
      if (!QuestionService.hasAnswer(answers, question.id)) {
        errors[question.id] = REQUIRED_FIELD_VALIDATION_ERROR;
        return errors;
      }
    }

    // Type-specific validation (email format, phone format, constraints, archived options)
    const error = validateSingleQuestion(question, value);
    if (error) {
      errors[question.id] = error;
    }

    return errors;
  };
};

// Simple wrapper for verification questions
type VerificationQuestionItemProps = {
  question: AF.Question<AF.WithId>;
  originalAnswer: any;
  onValueChange: (questionId: string, value: any) => void;
  onValidationChange: (questionId: string, hasError: boolean) => void;
  canEditVerificationData?: boolean;
  formId: string;
  // AI-related props
  aiSuggestion?: AIValidationResult | null;
  identityAiMatch?: IdentityAnalyzeIdMatch | null;
  onFindInDocument?: (polygons: readonly (readonly Point[])[]) => void;
};

// Helper component to handle value sync
const FormikSyncComponent: React.FC<{
  questionId: string;
  originalAnswer: any;
  onValueChange: (questionId: string, value: any) => void;
  formik: any;
}> = ({ questionId, originalAnswer, onValueChange, formik }) => {
  const currentValue = formik.values[questionId];

  React.useEffect(() => {
    if (!isEqual(currentValue, originalAnswer)) {
      onValueChange(questionId, currentValue);
    }
  }, [currentValue, originalAnswer, onValueChange, questionId]);

  return null;
};

// Helper component to sync validation errors to parent
const ValidationSyncComponent: React.FC<{
  questionId: string;
  onValidationChange: (questionId: string, hasError: boolean) => void;
  formik: any;
}> = ({ questionId, onValidationChange, formik }) => {
  const hasError = Boolean(formik.errors[questionId]);

  React.useEffect(() => {
    onValidationChange(questionId, hasError);
  }, [hasError, questionId, onValidationChange]);

  return null;
};

const VerificationQuestionItem: React.FC<VerificationQuestionItemProps> = ({
  question,
  originalAnswer,
  onValueChange,
  onValidationChange,
  canEditVerificationData,
  formId,
  aiSuggestion,
  identityAiMatch,
  onFindInDocument,
}) => {
  const questionTitle = (question as any).question || "Untitled Question";

  // Create a local Formik context for editable questions
  if (canEditVerificationData && question.type !== "FileUpload") {
    return (
      <Box key={question.id} mb={4}>
        <Formik
          initialValues={{ [question.id]: originalAnswer }}
          onSubmit={() => {}}
          enableReinitialize={true}
          validate={createQuestionValidator(question)}
          validateOnChange={true}
          validateOnBlur={true}
        >
          {(formik) => (
            <FormikProvider value={formik}>
              <FormikSyncComponent
                questionId={question.id}
                originalAnswer={originalAnswer}
                onValueChange={onValueChange}
                formik={formik}
              />
              <ValidationSyncComponent
                questionId={question.id}
                onValidationChange={onValidationChange}
                formik={formik}
              />
              <Question
                {...createQuestionProps(question, formId, questionTitle)}
              />

              {identityAiMatch && (
                <IdentityDocumentMatchHint
                  documentMatchText={identityAiMatch.documentMatchText}
                  matchHighlights={identityAiMatch.matchHighlights}
                  onFindInDocument={onFindInDocument}
                />
              )}

              {/* Add AI suggestion display - only show for field_relevance >= 50 and non-empty values */}
              {(() => {
                // Use certainty as fallback for old results missing field_relevance
                const fieldRelevance =
                  aiSuggestion?.field_relevance ?? aiSuggestion?.certainty ?? 0;

                if (!aiSuggestion || fieldRelevance < 50) {
                  return null;
                }

                const suggestedValue = getPreferredValue(aiSuggestion);

                // Don't show empty suggestions
                if (!suggestedValue || suggestedValue.trim() === "") {
                  return null;
                }

                return (
                  <AISuggestion
                    suggestedValue={suggestedValue}
                    currentValue={formik.values[question.id]}
                    certainty={aiSuggestion.certainty}
                    type="suggestion"
                    isEditable={canEditVerificationData}
                    questionType={question.type}
                    address_components={aiSuggestion.address_components}
                    onApplySuggestion={(value: any) => {
                      let valueToSet = value;

                      // For Address questions, use parsed components if available
                      if (
                        question.type === AF.AddressType &&
                        aiSuggestion?.address_components
                      ) {
                        valueToSet = {
                          street_address:
                            aiSuggestion.address_components.street1 || "",
                          street_address_line_2:
                            aiSuggestion.address_components.street2 || "",
                          city: aiSuggestion.address_components.city || "",
                          state: aiSuggestion.address_components.state || "",
                          zip_code:
                            aiSuggestion.address_components.postal_code || "",
                        };
                      }

                      formik.setFieldValue(question.id, valueToSet);
                      onValueChange(question.id, valueToSet);
                    }}
                  />
                );
              })()}
            </FormikProvider>
          )}
        </Formik>
      </Box>
    );
  }

  // For non-editable questions, use the standard display component
  return (
    <Box key={question.id} mb={4}>
      <Text fontSize="sm" color="gray.700" mb={2}>
        {questionTitle}
      </Text>
      {question.type === "FileUpload" ? (
        <Text fontSize="sm" fontWeight="medium">
          {originalAnswer || "N/A"}{" "}
          <em>(File uploads not editable in verification)</em>
        </Text>
      ) : (
        <AdminQuestionAnswer
          question={question}
          answer={originalAnswer}
          formId={formId}
        />
      )}
      {identityAiMatch && (
        <IdentityDocumentMatchHint
          documentMatchText={identityAiMatch.documentMatchText}
          matchHighlights={identityAiMatch.matchHighlights}
          onFindInDocument={onFindInDocument}
        />
      )}
    </Box>
  );
};

export const VerificationDetails: React.FC<VerificationDetailsProps> = ({
  verification,
  questions,
  answers,
  status,
  onStatusChange,
  formId,
  profiles,
  canEditVerificationData,
  onAnswerUpdate,
  onClose,
  aiResults,
  aiLoading = false,
  aiError = false,
  identityAiEnabled = false,
  identityAiLoading = false,
  identityAiError = false,
  identityAiAnswerMatches = [],
  identityAiProfileMatches = [],
  onFindInDocument,
}) => {
  const tooltipVerifyText =
    "If not, input the right value if you know it or reach out to the family for the correct information";

  // Keep track of changed answers locally
  const [pendingChanges, setPendingChanges] = useState<Record<string, any>>({});
  // Track local status selection (will be applied on save)
  const [localStatus, setLocalStatus] =
    useState<GQL.verification_status_enum | null>(status);
  // Track validation errors across all questions
  const [validationErrors, setValidationErrors] = useState<
    Record<string, boolean>
  >({});

  // Check if there are any validation errors
  const hasValidationErrors = Object.values(validationErrors).some(
    (hasError) => hasError
  );

  // Handle changes to individual question values
  const handleValueChange = React.useCallback(
    (questionId: string, value: any) => {
      setPendingChanges((prev) => ({
        ...prev,
        [questionId]: value,
      }));
    },
    []
  );

  // Handle validation error changes from child components
  const handleValidationChange = React.useCallback(
    (questionId: string, hasError: boolean) => {
      setValidationErrors((prev) => ({
        ...prev,
        [questionId]: hasError,
      }));
    },
    []
  );

  // Handle save button click
  const handleSave = () => {
    // Prevent save if there are validation errors
    if (hasValidationErrors) {
      return;
    }

    // Only submit if there are changes
    if (Object.keys(pendingChanges).length > 0) {
      // Apply each change individually
      Object.entries(pendingChanges).forEach(([questionId, value]) => {
        onAnswerUpdate(questionId, value, true); // Skip auto-verification since we handle it manually
      });
    }

    // Apply status change if it's changed
    if (localStatus !== status) {
      onStatusChange(localStatus!);
    }

    // Use informational toast when closing after save
    onClose({ showToast: true, toastType: "info" });
  };

  // Filter AI results to only include questions that exist in current verification group
  const filteredAiResults = useMemo(() => {
    if (!aiResults?.validation_results?.results) {
      return null;
    }

    // Create a set of question IDs in this verification for fast lookup, excluding FileUpload questions
    // This matches the UI filter: questions.filter((q) => q.type !== "FileUpload")
    const visibleQuestions = questions.filter((q) => q.type !== "FileUpload");
    const questionIds = new Set(visibleQuestions.map((q) => q.id));

    // Filter AI results to only include non-FileUpload questions from this verification
    // PLUS profile questions (which start with "person:")
    const filteredResults = aiResults.validation_results.results.filter(
      (result) => {
        // Include if question is in this verification OR it's a profile question
        return (
          questionIds.has(result.question_id) ||
          result.question_id.startsWith("person:")
        );
      }
    );

    return {
      ...aiResults,
      validation_results: {
        ...aiResults.validation_results,
        results: filteredResults,
      },
    };
  }, [aiResults, questions]);

  // Create a map of AI results by question ID for easy lookup
  const aiResultsMap = useMemo(() => {
    if (!filteredAiResults?.validation_results?.results) {
      return {};
    }

    const map: Record<string, AIValidationResult> = {};

    filteredAiResults.validation_results.results.forEach((result) => {
      if (result.question_id) {
        const existing = map[result.question_id];
        // Keep this result if no existing OR this has higher certainty
        if (!existing || result.certainty > existing.certainty) {
          map[result.question_id] = result;
        }
      }
    });

    return map;
  }, [filteredAiResults]);

  const userPermissions = useUserPermissions();

  return (
    <Flex direction="column" flex="2" h="100%" bg="white">
      {/* Fixed header with title */}
      <Box p={6} pb={3}>
        <Flex alignItems="center" mb={6} justifyContent="flex-start">
          <Text fontSize="36px" fontWeight="700" color="black">
            {verification.label} verification
          </Text>
        </Flex>

        {/* Avela Intelligence Alert */}
        <AvelaIntelligenceAlert
          aiResults={filteredAiResults || null}
          isLoading={aiLoading}
          hasError={aiError}
        />

        <IdentityIntelligenceAlert
          isEnabled={identityAiEnabled}
          isLoading={identityAiLoading}
          hasError={identityAiError}
          hasMatches={
            identityAiAnswerMatches.length > 0 ||
            identityAiProfileMatches.length > 0
          }
        />
      </Box>

      {/* Scrollable questions section */}
      <Box
        flex="1"
        overflowY="auto"
        px={6}
        pb={3}
        borderBottom="1px"
        borderColor="gray.300"
      >
        {/* Render verification questions from the verification group */}
        {questions
          .filter((q) => q.type !== "FileUpload")
          .map((question, qIndex) => {
            const aiSuggestion = aiResultsMap[question.id] || null;

            return (
              <VerificationQuestionItem
                key={question.id}
                question={question}
                originalAnswer={answers[question.id] ?? ""}
                onValueChange={handleValueChange}
                onValidationChange={handleValidationChange}
                canEditVerificationData={canEditVerificationData}
                formId={formId}
                aiSuggestion={aiSuggestion}
                identityAiMatch={identityAiAnswerMatches.find(
                  (match) => match.id === question.id
                )}
                onFindInDocument={onFindInDocument}
              />
            );
          })}

        {/* Verification question - moved to here as per the design */}
        <Box mt={6} mb={4}>
          <Flex direction="column" gap={3}>
            <Flex alignItems="center">
              <Text fontSize="md">
                Is the {verification.label.toLowerCase()} information correct?
              </Text>
              <Popover placement="right-end" trigger="hover">
                <PopoverTrigger>
                  <IconButton
                    aria-label="Verification Information"
                    size="sm"
                    icon={<InfoOutlineIcon />}
                    variant="ghost"
                    color="black"
                    ml={1}
                  />
                </PopoverTrigger>
                <PopoverContent>
                  <Box margin={6}>
                    <Tag size="md" variant="solid" colorScheme="primary" mb={2}>
                      <Icon boxSize={4} as={RiLightbulbLine} paddingRight={1} />
                      <Text size="xs">Tip</Text>
                    </Tag>
                    <Heading
                      as="h3"
                      fontSize="md"
                      fontWeight="700"
                      color="gray.700"
                      mb={2}
                    >
                      Verification Information
                    </Heading>
                    <Text fontWeight="400" color="gray.700">
                      {tooltipVerifyText}
                    </Text>
                  </Box>
                </PopoverContent>
              </Popover>
            </Flex>
            <Flex gap={2}>
              <Button
                isDisabled={!userPermissions.hasOne("form:update")}
                size="sm"
                leftIcon={<RiCloseLine />}
                variant="outline"
                borderColor="gray.300"
                bg={
                  localStatus === GQL.verification_status_enum.Rejected
                    ? "gray.100"
                    : "white"
                }
                color="gray.600"
                fontWeight="600"
                onClick={() => {
                  setLocalStatus(
                    localStatus === GQL.verification_status_enum.Rejected
                      ? GQL.verification_status_enum.Pending
                      : GQL.verification_status_enum.Rejected
                  );
                }}
              >
                No
              </Button>
              <Button
                isDisabled={!userPermissions.hasOne("form:update")}
                size="sm"
                leftIcon={<RiCheckLine />}
                variant="outline"
                borderColor="gray.300"
                bg={
                  localStatus === GQL.verification_status_enum.Verified
                    ? "gray.100"
                    : "white"
                }
                color="gray.600"
                fontWeight="600"
                onClick={() => {
                  setLocalStatus(
                    localStatus === GQL.verification_status_enum.Verified
                      ? GQL.verification_status_enum.Pending
                      : GQL.verification_status_enum.Verified
                  );
                }}
              >
                Yes
              </Button>
            </Flex>
          </Flex>
        </Box>
      </Box>

      {/* Family tabs scrollable section */}
      <Box
        flex="1"
        overflowY="auto"
        borderBottom="1px"
        borderColor="gray.300"
        bg="gray.50"
        p={0}
      >
        <FamilyTabs
          profiles={profiles}
          aiResultsMap={aiResultsMap}
          formId={formId}
          identityAiProfileMatches={identityAiProfileMatches}
          onFindInDocument={onFindInDocument}
        />
      </Box>

      {/* Action bar at the bottom */}
      <Flex
        justifyContent="space-between"
        p={4}
        borderTop="1px"
        borderColor="gray.300"
      >
        <Button
          variant="outline"
          onClick={() => onClose()}
          fontFamily="Inter"
          fontWeight={600}
          fontSize="16px"
          lineHeight="24px"
          letterSpacing="0%"
          borderColor="gray.300"
          color="gray.700"
        >
          Close
        </Button>
        <Button
          colorScheme="primary"
          isDisabled={
            localStatus === null ||
            !canEditVerificationData ||
            hasValidationErrors
          }
          onClick={handleSave}
        >
          Save
        </Button>
      </Flex>
    </Flex>
  );
};
